import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { useLazyGetPacingLookupQuery } from '../endpoints/pacing-board.endpoints';
import { PacingLookupItem, PacingLookupType } from '../endpoints/pacing-lookup.types';

const ENTITY_LABELS: Record<PacingLookupType, string> = {
  client: 'Client',
  campaign: 'Campaign',
  io: 'Insertion Order',
};

interface PacingEntityTypeaheadProps {
  entityType: PacingLookupType;
  value: string;
  displayValue: string;
  selectedItem?: PacingLookupItem | null;
  onInputChange: (value: string) => void;
  onSelect: (item: PacingLookupItem | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const PacingEntityTypeahead: React.FC<PacingEntityTypeaheadProps> = ({
  entityType,
  value,
  displayValue,
  selectedItem,
  onInputChange,
  onSelect,
  placeholder,
  disabled = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [triggerLookup, { data, isFetching }] = useLazyGetPacingLookupQuery();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const items = data?.items ?? [];
  const trimmedValue = value.trim();
  const hasExactMatch = useMemo(
    () => items.some((item) => item.label.toLowerCase() === trimmedValue.toLowerCase()),
    [items, trimmedValue]
  );
  const showNoMatch = trimmedValue.length > 0 && !hasExactMatch && !isFetching;

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!isOpen) {
      return undefined;
    }

    debounceRef.current = setTimeout(() => {
      triggerLookup({ type: entityType, q: trimmedValue });
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [entityType, isOpen, trimmedValue, triggerLookup]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (item: PacingLookupItem) => {
    onSelect(item);
    setIsOpen(false);
  };

  const handleClear = () => {
    onSelect(null);
    onInputChange('');
    setIsOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
    >
      <div className="relative">
        <input
          type="text"
          className="w-full px-3 py-2 rounded-xl border border-gray-300 bg-white text-sm h-10 focus:outline-none focus:ring-0 pr-16"
          value={displayValue}
          placeholder={placeholder ?? `Search ${ENTITY_LABELS[entityType].toLowerCase()}…`}
          disabled={disabled}
          autoComplete="off"
          onChange={(event) => {
            onInputChange(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {(displayValue || selectedItem) && !disabled && (
            <button
              type="button"
              className="p-1 hover:bg-gray-100 rounded-full"
              onClick={handleClear}
              tabIndex={-1}
            >
              <X className="h-3 w-3 text-gray-400" />
            </button>
          )}
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {isFetching && (
            <div className="px-3 py-2 text-sm text-gray-500">
              Searching…
            </div>
          )}
          {!isFetching && items.map((item) => (
            <button
              key={item.id}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-gray-100 transition-colors"
              onClick={() => handleSelect(item)}
            >
              <div className="font-medium text-gray-900">
                {item.label}
              </div>
              {item.subtitle && (
                <div className="text-xs text-gray-500 mt-1">
                  {item.subtitle}
                </div>
              )}
            </button>
          ))}
          {!isFetching && items.length >= 150 && (
            <div className="px-3 py-2 text-xs text-gray-500 border-t border-gray-200">
              Showing the first 150 results — type to narrow the search
            </div>
          )}
          {showNoMatch && (
            <div className="px-3 py-2 text-sm text-gray-600 border-t border-gray-200">
              {entityType === 'client'
                ? 'No matches — will be added as new Client'
                : 'No matches found'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PacingEntityTypeahead;
